import React, { FC, useCallback, useState } from 'react'
import { useDisclosure } from '@chakra-ui/react'
import { Trans, t } from '@lingui/macro'
import { useClipboard } from 'use-clipboard-copy'

import {
  Box,
  Flex,
  StackDivider,
  Text,
  VStack,
  ButtonClean,
  ButtonDanger,
  ButtonPrimary,
  Switch,
  ButtonIcon,
  Tooltip,
  Modal
} from 'front-end/src/components'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'
import { brandColors } from 'shared/basicPalette'
import IconPlus2 from 'front-end/src/components/common/icon/IconPlus2'
import IconX from 'front-end/src/components/common/icon/IconX'
import { notification } from 'front-end/src/components/common/notifications/Notifications'
import {
  OrganisationTokenModel,
  OrganisationTokenModelFilterEnum,
  OrganisationTokenPrivilege
} from 'front-end/src/graphql/private/PrivateSchemaTypes'

import { Checkbox } from '../../../components/common/form/checkbox/Checkbox'
import dayjs from 'shared/dayjs'
import {
  useCreateOrganisationTokenMutation,
  useFetchOrganisationTokensQuery,
  useDeleteScimTokenMutation,
  useSetScimArchiveOnDeactivateMutation
} from './scimConfig.codegen'

import './ScimConfig.less'
import IconCopy from 'front-end/src/components/common/icon/IconCopy'

export const ScimConfig: FC = () => {
  const response = useFetchOrganisationTokensQuery({
    variables: {
      filters: [
        { name: OrganisationTokenModelFilterEnum.includeDeleted },
        { name: OrganisationTokenModelFilterEnum.privileges, value: 'SCIM' }
      ]
    }
  })
  const { loading, data, refetch } = response
  const organisationTokens = data?.admin?.organisation?.organisationTokens
  const organisation = data?.admin?.organisation

  const [deleteTokenMutation] = useDeleteScimTokenMutation()
  const [createTokenMutation] = useCreateOrganisationTokenMutation()
  const [changeArchiveSettingMutation] = useSetScimArchiveOnDeactivateMutation()
  const { isOpen, onOpen, onClose } = useDisclosure()

  const [deletedToken, setDeletedToken] = useState<
    | ({ __typename?: 'OrganisationTokenModel' } & Pick<
        OrganisationTokenModel,
        | 'id'
        | 'token'
        | 'privileges'
        | 'created_at'
        | 'deleted_at'
        | 'scimResourcesCount'
      >)
    | null
  >(null)
  const [showWholeTokens, setShowWholeTokens] = useState(false)
  const clipboard = useClipboard()

  const onChangeArchiveSetting = useCallback(
    async (value: boolean) => {
      const confirmed = confirm(
        t`Are you sure you want to change this setting?`
      )
      if (!confirmed) {
        return
      }

      await changeArchiveSettingMutation({
        variables: { value }
      })
      await refetch()
    },
    [changeArchiveSettingMutation, refetch]
  )

  if (loading) {
    return (
      <span>
        <Trans>Loading...</Trans>
      </span>
    )
  }

  const scimResourcesCount = deletedToken?.scimResourcesCount ?? 0

  const deleteTokenAndCloseModal = async (archiveResources?: boolean) => {
    await deleteTokenMutation({
      variables: {
        tokenId: deletedToken?.id as number,
        archiveResources
      }
    })
    await refetch()
    onClose()
  }

  return (
    <div className="AccountPage__scim">
      <div className="text-description">
        SCIM <Trans>Endpoint</Trans>:{' '}
        <pre>{window.location.origin}/scim/v2</pre>
      </div>
      <div className="AccountPage__checkBoxWrapper">
        <Checkbox
          id="scim_archive_on_deactivate"
          isChecked={!!organisation?.scim_archive_on_deactivate}
          onChange={(e) => onChangeArchiveSetting(e.target.checked)}
        >
          <Trans>Archive user when deactivated via SCIM</Trans>
        </Checkbox>
      </div>
      <Flex mt={'16px'} justify="space-between" alignItems="flex-end">
        <h4>
          <Trans>Tokens</Trans>
        </h4>
        <Flex flexDirection="column" alignItems="flex-end">
          <ButtonPrimary
            ml={'6px'}
            disabled={loading}
            onClick={async () => {
              await createTokenMutation({
                variables: {
                  privileges: [OrganisationTokenPrivilege.SCIM]
                }
              })
              refetch()
            }}
            leftIcon={<IconPlus2 />}
          >
            <Trans>Create token</Trans>
          </ButtonPrimary>
          <Flex className="text-description" alignItems="center">
            <Text mr={'6px'}>
              <Trans>Show whole tokens (use in discretion)</Trans>
            </Text>
            <Switch
              onChange={() => {
                setShowWholeTokens(!showWholeTokens)
              }}
              isChecked={showWholeTokens}
            />
          </Flex>
        </Flex>
      </Flex>

      <div className="AccountPage__scim__actions">
        <VStack
          divider={<StackDivider borderColor="gray.800" />}
          spacing={'4px'}
          align="stretch"
        >
          {organisationTokens?.map((token) => {
            const isDeleted = !!token.deleted_at
            return (
              <Flex
                align="center"
                p={'8px'}
                flexGrow={1}
                minH="60px"
                key={token.id}
                borderRadius="md"
                backgroundColor={brandColors.grey.lightest}
                opacity={isDeleted ? 0.6 : 1}
              >
                {!isDeleted ? (
                  <ButtonIcon
                    icon={<IconCopy boxSize="16px" />}
                    hint={t`Copy to clipboard`}
                    aria-label={t`Copy to clipboard`}
                    color={brandColors.grey[500]}
                    onClick={() => {
                      clipboard.copy(token.token)
                      notification.info(t`Successfully copied to clipboard`)
                    }}
                  />
                ) : (
                  <Tooltip text={t`Deleted`}>
                    <IconX m={'0 10px'} />
                  </Tooltip>
                )}
                <Box fontSize={fontSizes.sm} flexGrow={1}>
                  <code>
                    {showWholeTokens
                      ? token.token
                      : `${token.token?.substring(0, 16)}...`}
                  </code>
                </Box>
                {
                  <Box fontSize={fontSizes.sm} color={brandColors.grey.mid}>
                    <Trans>Created</Trans>: {dayjs(token.created_at).fromNow()}
                  </Box>
                }
                <Box ml="12px">
                  {!isDeleted ? (
                    <Flex>
                      <ButtonDanger
                        onClick={() => {
                          setDeletedToken(token)
                          onOpen()
                        }}
                      >
                        <Trans>Archive token</Trans>
                      </ButtonDanger>
                    </Flex>
                  ) : (
                    <Box
                      fontSize={fontSizes.sm}
                      mr={'8px'}
                      color={brandColors.grey.mid}
                      ml="4px"
                    >
                      <Trans>Deleted</Trans>:{' '}
                      {dayjs(token.deleted_at).fromNow()}
                    </Box>
                  )}
                </Box>
              </Flex>
            )
          })}
        </VStack>
      </div>
      <Modal
        show={isOpen}
        size="WIDE"
        header={t`Do you want to remove associated users and groups?`}
        subHeader={
          scimResourcesCount > 0
            ? t`There are ${scimResourcesCount} scim resources associated. What to do about them?`
            : t`There are no SCIM resources associated with this token.`
        }
        controls={
          <>
            <ButtonClean key={1} onClick={onClose}>
              <Trans>Cancel</Trans>
            </ButtonClean>
            {scimResourcesCount > 0 ? (
              <>
                <ButtonClean
                  key={2}
                  onClick={() => {
                    deleteTokenAndCloseModal(false)
                  }}
                >
                  <Trans>Keep users and groups</Trans>
                </ButtonClean>
                <ButtonDanger
                  key={2}
                  onClick={async () => {
                    deleteTokenAndCloseModal(true)
                  }}
                >
                  <Trans>Delete users and groups</Trans>
                </ButtonDanger>
              </>
            ) : (
              <ButtonDanger
                key={2}
                onClick={async () => {
                  deleteTokenAndCloseModal()
                }}
              >
                <Trans>Archive token</Trans>
              </ButtonDanger>
            )}
          </>
        }
      />
    </div>
  )
}
